import { z } from 'zod';
import {
  AUTOMATION_DESCRIPTION_MAX,
  AUTOMATION_MAX_ACTIONS,
  AUTOMATION_MAX_CONDITIONS,
  AUTOMATION_NAME_MAX,
  AutomationActionSchema,
  AutomationTriggerSchema,
  automationRuleIssues,
} from './automation';
import { CANNED_BODY_MAX_LENGTH, CannedVisibilitySchema } from './canned';
import {
  FieldConfigSchema,
  type FieldType,
  FieldTypeSchema,
  type FilterOp,
  FilterOpSchema,
  INDEXABLE_TYPES,
} from './fields/registry';
import { FormKindSchema, FormSystemFieldSchema } from './forms';
import {
  GranteeTypeSchema,
  type PermissionKey,
  PermissionKeySchema,
  SYSTEM_PROJECT_ROLES,
  TenantRoleSchema,
} from './permissions';
import {
  ColorSchema,
  IssueTypePortalSchema,
  PriorityUrgencySchema,
  ProjectSettingsPatchSchema,
} from './projects';
import { SYSTEM_FIELDS, type SystemField, SystemFieldSchema } from './query/ast';
import { RoleKeySchema } from './roles';
import { SlaCalendarSchema, SlaGoalSchema } from './sla';
import { ViewKindSchema } from './views';
import { ConditionRuleSchema, StatusSchema } from './workflow/rules';

/**
 * Portable project template (ADR-0013 §7, `aidlc/domain/project-template.md`). No ids and
 * no tenant data: every cross-reference is a local `ref`. Custom fields are referenced as
 * `fieldRef` (import rewrites them to generated `cf_` keys); system fields keep their
 * snake_case name; roles are referenced by key; select values are option refs; SLA goals
 * are keyed by priority name.
 */
export const TEMPLATE_VERSION = 1;

export const TEMPLATE_REF_PATTERN = /^[a-z][a-z0-9_-]{0,39}$/;
export const TemplateRefSchema = z
  .string()
  .regex(TEMPLATE_REF_PATTERN, 'local reference: lowercase letters, digits, _ or -');

/* ----------------------------- field targets ----------------------------- */

const leafProps = { op: FilterOpSchema, value: z.unknown().optional() };

/** `{ field, op, value? }` for system fields, `{ fieldRef, op, value? }` for custom fields. */
export const TemplateConditionLeafSchema = z.union([
  z.strictObject({ field: SystemFieldSchema, ...leafProps }),
  z.strictObject({ fieldRef: TemplateRefSchema, ...leafProps }),
]);
export type TemplateConditionLeaf = z.infer<typeof TemplateConditionLeafSchema>;

/* ----------------------------- rule grammar ----------------------------- */

export type TemplateConditionRule =
  | { type: 'has_permission'; permission: PermissionKey }
  | { type: 'user_is'; who: 'assignee' | 'reporter' | 'project_lead' }
  | { type: 'user_in_role'; roleKey: string }
  | { type: 'field_matches'; field: SystemField; op: FilterOp; value?: unknown }
  | { type: 'field_matches'; fieldRef: string; op: FilterOp; value?: unknown }
  | { all: TemplateConditionRule[] }
  | { any: TemplateConditionRule[] }
  | { not: TemplateConditionRule };

/** Workflow-rule conditions with portable references (`user_in_group` is not portable). */
export const TemplateConditionRuleSchema: z.ZodType<TemplateConditionRule> = z.lazy(() =>
  z.union([
    z.strictObject({ type: z.literal('has_permission'), permission: PermissionKeySchema }),
    z.strictObject({
      type: z.literal('user_is'),
      who: z.enum(['assignee', 'reporter', 'project_lead']),
    }),
    z.strictObject({ type: z.literal('user_in_role'), roleKey: RoleKeySchema }),
    z.strictObject({ type: z.literal('field_matches'), field: SystemFieldSchema, ...leafProps }),
    z.strictObject({ type: z.literal('field_matches'), fieldRef: TemplateRefSchema, ...leafProps }),
    z.strictObject({ all: z.array(TemplateConditionRuleSchema).max(20) }),
    z.strictObject({ any: z.array(TemplateConditionRuleSchema).min(1).max(20) }),
    z.strictObject({ not: TemplateConditionRuleSchema }),
  ]),
);

const message = z.string().max(200).optional();

export const TemplateValidatorSchema = z.union([
  z.strictObject({ type: z.literal('field_required'), field: SystemFieldSchema, message }),
  z.strictObject({ type: z.literal('field_required'), fieldRef: TemplateRefSchema, message }),
  z.strictObject({
    type: z.literal('field_matches'),
    field: SystemFieldSchema,
    ...leafProps,
    message,
  }),
  z.strictObject({
    type: z.literal('field_matches'),
    fieldRef: TemplateRefSchema,
    ...leafProps,
    message,
  }),
  z.strictObject({ type: z.literal('no_open_children'), message }),
  z.strictObject({
    type: z.literal('approval_required'),
    approverRoleKey: RoleKeySchema,
    message,
  }),
]);
export type TemplateValidator = z.infer<typeof TemplateValidatorSchema>;

export const TemplatePostFunctionSchema = z.union([
  z.strictObject({ type: z.literal('set_field'), field: SystemFieldSchema, value: z.unknown() }),
  z.strictObject({ type: z.literal('set_field'), fieldRef: TemplateRefSchema, value: z.unknown() }),
  z.strictObject({
    type: z.literal('assign'),
    to: z.enum(['reporter', 'current_user', 'unassigned']),
  }),
  z.strictObject({ type: z.literal('emit_event'), eventType: z.string().min(1).max(80) }),
  z.strictObject({
    type: z.literal('add_comment'),
    body: z.string().min(1).max(5000),
    visibility: z.enum(['public', 'internal']).default('internal'),
  }),
]);
export type TemplatePostFunction = z.infer<typeof TemplatePostFunctionSchema>;

/* ----------------------------- query grammar ----------------------------- */

export type TemplateFilterNode =
  | TemplateConditionLeaf
  | { and: TemplateFilterNode[] }
  | { or: TemplateFilterNode[] }
  | { not: TemplateFilterNode };

export const TemplateFilterNodeSchema: z.ZodType<TemplateFilterNode> = z.lazy(() =>
  z.union([
    z.strictObject({ field: SystemFieldSchema, ...leafProps }),
    z.strictObject({ fieldRef: TemplateRefSchema, ...leafProps }),
    z.strictObject({ and: z.array(TemplateFilterNodeSchema).min(1).max(50) }),
    z.strictObject({ or: z.array(TemplateFilterNodeSchema).min(1).max(50) }),
    z.strictObject({ not: TemplateFilterNodeSchema }),
  ]),
);

const dir = z.enum(['asc', 'desc']).default('asc');
export const TemplateSortSchema = z.union([
  z.strictObject({ field: SystemFieldSchema, dir }),
  z.strictObject({ fieldRef: TemplateRefSchema, dir }),
]);

const limitPerGroup = z.int().min(1).max(100).default(20);
export const TemplateGroupBySchema = z.union([
  z.strictObject({ field: SystemFieldSchema, limitPerGroup }),
  z.strictObject({ fieldRef: TemplateRefSchema, limitPerGroup }),
]);

/** Search AST with portable field references; `page` is not part of a saved view. */
export const TemplateSearchRequestSchema = z.strictObject({
  filter: TemplateFilterNodeSchema.optional(),
  sort: z.array(TemplateSortSchema).max(3).default([]),
  groupBy: TemplateGroupBySchema.optional(),
  select: z
    .array(z.union([SystemFieldSchema, z.strictObject({ fieldRef: TemplateRefSchema })]))
    .max(50)
    .optional(),
});
export type TemplateSearchRequest = z.infer<typeof TemplateSearchRequestSchema>;

/* ----------------------------- form grammar ----------------------------- */

const formFieldProps = {
  label: z.string().min(1).max(80).optional(),
  required: z.boolean().optional(),
  readOnly: z.boolean().optional(),
  visibleWhen: TemplateConditionLeafSchema.optional(),
};

export const TemplateFormFieldSchema = z.union([
  z.strictObject({ key: FormSystemFieldSchema, ...formFieldProps }),
  z.strictObject({ fieldRef: TemplateRefSchema, ...formFieldProps }),
]);
export type TemplateFormField = z.infer<typeof TemplateFormFieldSchema>;

export const TemplateFormLayoutSchema = z.strictObject({
  sections: z
    .array(
      z.strictObject({
        title: z.string().min(1).max(80).optional(),
        description: z.string().max(500).optional(),
        fields: z.array(TemplateFormFieldSchema).max(100),
      }),
    )
    .min(1)
    .max(20),
});
export type TemplateFormLayout = z.infer<typeof TemplateFormLayoutSchema>;

/* ----------------------------- document sections ----------------------------- */

export const TemplatePrioritySchema = z.strictObject({
  name: z.string().min(1).max(40),
  color: ColorSchema.nullable().default(null),
  rank: z.int().min(1).max(1000),
  urgency: PriorityUrgencySchema,
  isDefault: z.boolean().default(false),
});
export type TemplatePriority = z.infer<typeof TemplatePrioritySchema>;

export const TemplateFieldOptionSchema = z.strictObject({
  ref: TemplateRefSchema,
  label: z.string().min(1).max(80),
  color: ColorSchema.nullable().default(null),
});
export type TemplateFieldOption = z.infer<typeof TemplateFieldOptionSchema>;

export const TemplateFieldSchema = z.strictObject({
  ref: TemplateRefSchema,
  type: FieldTypeSchema,
  label: z.string().min(1).max(80),
  description: z.string().max(500).nullable().default(null),
  /** Type-specific options; validated against `FieldConfigSchema` for `type`. */
  config: z.record(z.string(), z.unknown()).default({}),
  indexed: z.boolean().default(false),
  enforceUnique: z.boolean().default(false),
  /** Required for single_select / multi_select, forbidden otherwise. */
  options: z.array(TemplateFieldOptionSchema).max(200).optional(),
});
export type TemplateField = z.infer<typeof TemplateFieldSchema>;

export const TemplateFieldAccessSchema = z.strictObject({
  readRoles: z.array(RoleKeySchema).max(50).nullable(),
  editRoles: z.array(RoleKeySchema).max(50).nullable(),
});

export const TemplateBindingSchema = z.strictObject({
  fieldRef: TemplateRefSchema,
  required: z.boolean().default(false),
  /** Option ref(s) for select fields; a plain value otherwise. */
  defaultValue: z.unknown().optional(),
  position: z.int().min(0),
  access: TemplateFieldAccessSchema.nullable().default(null),
  /** Null = all issue types. */
  issueTypeRefs: z.array(TemplateRefSchema).max(50).nullable().default(null),
});
export type TemplateBinding = z.infer<typeof TemplateBindingSchema>;

export const TemplateIssueTypeSchema = z.strictObject({
  ref: TemplateRefSchema,
  name: z.string().min(1).max(60),
  description: z.string().max(500).nullable().default(null),
  icon: z.string().max(40).nullable().default(null),
  workflowRef: TemplateRefSchema,
  isSubtask: z.boolean().default(false),
  position: z.int().min(0),
  portal: IssueTypePortalSchema.prefault({}),
});
export type TemplateIssueType = z.infer<typeof TemplateIssueTypeSchema>;

export const TemplateStatusSchema = StatusSchema.omit({ id: true }).extend({
  ref: TemplateRefSchema,
});
export type TemplateStatus = z.infer<typeof TemplateStatusSchema>;

export const TemplateTransitionSchema = z.strictObject({
  key: z.string().min(1).max(40),
  name: z.string().min(1).max(60),
  /** Null = available from any status; `[]` only for the initial (create) transition. */
  fromStatusRefs: z.array(TemplateRefSchema).max(100).nullable(),
  toStatusRef: TemplateRefSchema,
  isInitial: z.boolean().default(false),
  conditions: TemplateConditionRuleSchema.default({ all: [] }),
  validators: z.array(TemplateValidatorSchema).max(20).default([]),
  postFunctions: z.array(TemplatePostFunctionSchema).max(20).default([]),
  position: z.int().min(0),
});
export type TemplateTransition = z.infer<typeof TemplateTransitionSchema>;

export const TemplateWorkflowSchema = z.strictObject({
  ref: TemplateRefSchema,
  name: z.string().min(1).max(80),
  initialStatusRef: TemplateRefSchema,
  statuses: z.array(TemplateStatusSchema).min(1).max(100),
  transitions: z.array(TemplateTransitionSchema).max(300),
});
export type TemplateWorkflow = z.infer<typeof TemplateWorkflowSchema>;

export const TemplateFormSchema = z.strictObject({
  kind: FormKindSchema,
  issueTypeRef: TemplateRefSchema.nullable().default(null),
  name: z.string().min(1).max(80),
  layout: TemplateFormLayoutSchema,
});
export type TemplateForm = z.infer<typeof TemplateFormSchema>;

export const TemplateRoleSchema = z.strictObject({
  key: RoleKeySchema,
  name: z.string().min(1).max(80),
});

/**
 * `granteeRoleKey` for `project_role`, `granteeTenantRole` for `tenant_role`, nothing for
 * dynamic grantees. `user` and `group` grants are tenant-specific and not portable.
 */
export const TemplatePermissionGrantSchema = z.strictObject({
  permission: PermissionKeySchema,
  granteeType: GranteeTypeSchema,
  granteeRoleKey: RoleKeySchema.optional(),
  granteeTenantRole: TenantRoleSchema.optional(),
});
export type TemplatePermissionGrant = z.infer<typeof TemplatePermissionGrantSchema>;

export const TemplateViewSchema = z.strictObject({
  name: z.string().min(1).max(80),
  kind: ViewKindSchema,
  query: TemplateSearchRequestSchema,
  shared: z.boolean().default(true),
  portalVisible: z.boolean().default(false),
  position: z.int().min(0).default(0),
});
export type TemplateView = z.infer<typeof TemplateViewSchema>;

const templateConditions = z.array(TemplateConditionRuleSchema).max(20).default([]);

export const TemplateSlaPolicySchema = z.strictObject({
  name: z.string().min(1).max(80),
  /** Keyed by priority name (by priority id in the database). */
  goals: z.record(z.string().min(1).max(40), SlaGoalSchema),
  startConditions: templateConditions,
  pauseConditions: templateConditions,
  stopConditions: templateConditions,
  calendar: SlaCalendarSchema.nullable().default(null),
  enabled: z.boolean().default(true),
});
export type TemplateSlaPolicy = z.infer<typeof TemplateSlaPolicySchema>;

export const TemplateCannedResponseSchema = z.strictObject({
  name: z.string().min(1).max(80),
  body: z.string().min(1).max(CANNED_BODY_MAX_LENGTH),
  visibility: CannedVisibilitySchema.default('public'),
  position: z.int().min(0).default(0),
});

/**
 * A seeded automation rule (S-07-11): the real trigger/conditions/action grammar, not a
 * `ref`-indirected one — every trigger and action this bolt ships in template JSON
 * (`event` triggers, `notify` with the dynamic `reporter`/`assignee`/`watchers` recipients)
 * carries no ids of its own, so there is nothing to resolve at import time. `isSystem: true`
 * (S-07-11's "visible, disable-able, not deletable") is how a seeded rule marks itself; a
 * template author writing a rule that *does* need a real id (a `role`/`user` recipient, a
 * `webhook` subscription, …) has to accept that the id travels through export/import verbatim —
 * a known simplification, not a portability guarantee for every action type.
 */
export const TemplateAutomationRuleSchema = z
  .strictObject({
    name: z.string().min(1).max(AUTOMATION_NAME_MAX),
    description: z.string().max(AUTOMATION_DESCRIPTION_MAX).nullable().default(null),
    enabled: z.boolean().default(true),
    isSystem: z.boolean().default(false),
    trigger: AutomationTriggerSchema,
    conditions: z.array(ConditionRuleSchema).max(AUTOMATION_MAX_CONDITIONS).default([]),
    actions: z.array(AutomationActionSchema).min(1).max(AUTOMATION_MAX_ACTIONS),
    position: z.int().min(0).default(0),
  })
  .superRefine((rule, ctx) => {
    for (const issue of automationRuleIssues(rule)) {
      ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path.split('.') });
    }
  });
export type TemplateAutomationRule = z.infer<typeof TemplateAutomationRuleSchema>;

const ProjectTemplateDocumentBaseSchema = z.strictObject({
  version: z.literal(TEMPLATE_VERSION),
  project: z
    .strictObject({
      name: z.string().min(1).max(120).optional(),
      category: z.string().max(80).nullable().optional(),
      settings: ProjectSettingsPatchSchema.optional(),
    })
    .prefault({}),
  priorities: z.array(TemplatePrioritySchema).max(20).default([]),
  fields: z.array(TemplateFieldSchema).max(200).default([]),
  bindings: z.array(TemplateBindingSchema).max(200).default([]),
  issueTypes: z.array(TemplateIssueTypeSchema).max(50).default([]),
  workflows: z.array(TemplateWorkflowSchema).max(50).default([]),
  forms: z.array(TemplateFormSchema).max(100).default([]),
  roles: z.array(TemplateRoleSchema).max(50).default([]),
  permissionGrants: z.array(TemplatePermissionGrantSchema).max(500).default([]),
  views: z.array(TemplateViewSchema).max(50).default([]),
  slaPolicies: z.array(TemplateSlaPolicySchema).max(20).default([]),
  cannedResponses: z.array(TemplateCannedResponseSchema).max(100).default([]),
  automations: z.array(TemplateAutomationRuleSchema).max(50).default([]),
});
export type ProjectTemplateDocument = z.infer<typeof ProjectTemplateDocumentBaseSchema>;
export type ProjectTemplateDocumentInput = z.input<typeof ProjectTemplateDocumentBaseSchema>;

/* ----------------------------- reference validation ----------------------------- */

export interface TemplateIssue {
  path: (string | number)[];
  message: string;
}
type Path = (string | number)[];

const SELECT_TYPES: ReadonlySet<FieldType> = new Set<FieldType>(['single_select', 'multi_select']);
const SYSTEM_FIELD_NAMES: ReadonlySet<string> = new Set<string>(SYSTEM_FIELDS);
const LIST_VALUE_OPS: ReadonlySet<FilterOp> = new Set<FilterOp>([
  'in',
  'not_in',
  'any_of',
  'all_of',
  'none_of',
]);
const SCALAR_VALUE_OPS: ReadonlySet<FilterOp> = new Set<FilterOp>(['eq', 'neq']);

const issuePath = (path: PropertyKey[]): Path =>
  path.filter((p): p is string | number => typeof p !== 'symbol');

/**
 * Structural checks that Zod's shape validation cannot express: every `*Ref` resolves,
 * refs and keys are unique, select values are option refs, field configs match their type,
 * grants carry the right grantee. Pure; shared by the schema's `superRefine` and the API.
 */
export function findTemplateIssues(doc: ProjectTemplateDocument): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  const add = (path: Path, msg: string): void => {
    issues.push({ path, message: msg });
  };

  // Priorities
  const priorityNames = new Set<string>();
  let defaults = 0;
  doc.priorities.forEach((p, i) => {
    if (priorityNames.has(p.name)) {
      add(['priorities', i, 'name'], `duplicate priority ${p.name}`);
    }
    priorityNames.add(p.name);
    if (p.isDefault) {
      defaults += 1;
    }
  });
  if (defaults > 1) {
    add(['priorities'], 'at most one default priority');
  }

  // Roles (system roles are always available)
  const roles = new Set<string>(SYSTEM_PROJECT_ROLES);
  const declaredRoles = new Set<string>();
  doc.roles.forEach((r, i) => {
    if (declaredRoles.has(r.key)) {
      add(['roles', i, 'key'], `duplicate role key ${r.key}`);
    }
    declaredRoles.add(r.key);
    roles.add(r.key);
  });
  const checkRole = (key: string, path: Path): void => {
    if (!roles.has(key)) {
      add(path, `unknown role key ${key}`);
    }
  };

  // Fields and options
  const fields = new Map<string, TemplateField>();
  doc.fields.forEach((f, i) => {
    const path: Path = ['fields', i];
    if (fields.has(f.ref)) {
      add([...path, 'ref'], `duplicate field ref ${f.ref}`);
    } else if (SYSTEM_FIELD_NAMES.has(f.ref)) {
      add([...path, 'ref'], `field ref ${f.ref} collides with a system field`);
    }
    fields.set(f.ref, f);
    const cfg = FieldConfigSchema.safeParse({ ...f.config, type: f.type });
    if (!cfg.success) {
      for (const issue of cfg.error.issues) {
        add([...path, 'config', ...issuePath(issue.path)], issue.message);
      }
    }
    if (f.options && !SELECT_TYPES.has(f.type)) {
      add([...path, 'options'], 'options are only valid for select fields');
    }
    if (SELECT_TYPES.has(f.type) && (!f.options || f.options.length === 0)) {
      add([...path, 'options'], 'select fields need at least one option');
    }
    const optionRefs = new Set<string>();
    f.options?.forEach((o, j) => {
      if (optionRefs.has(o.ref)) {
        add([...path, 'options', j, 'ref'], `duplicate option ref ${o.ref}`);
      }
      optionRefs.add(o.ref);
    });
    if (f.enforceUnique && !f.indexed) {
      add([...path, 'enforceUnique'], 'enforceUnique requires indexed');
    }
    if (f.indexed && !INDEXABLE_TYPES.has(f.type)) {
      add([...path, 'indexed'], `${f.type} fields cannot be indexed`);
    }
  });

  const optionRefsOf = (f: TemplateField): Set<string> =>
    new Set((f.options ?? []).map((o) => o.ref));

  /** A stored value (default, set_field) for a select field must be option ref(s). */
  const checkStoredValue = (f: TemplateField, value: unknown, path: Path): void => {
    if (!SELECT_TYPES.has(f.type) || value === undefined || value === null) {
      return;
    }
    const refs = optionRefsOf(f);
    if (f.type === 'single_select') {
      if (typeof value !== 'string' || !refs.has(value)) {
        add(path, `unknown option ref for field ${f.ref}`);
      }
      return;
    }
    if (!Array.isArray(value)) {
      add(path, `multi_select values must be an array of option refs`);
      return;
    }
    value.forEach((v, i) => {
      if (typeof v !== 'string' || !refs.has(v)) {
        add([...path, i], `unknown option ref for field ${f.ref}`);
      }
    });
  };

  /** A filter value for a select field must be option ref(s) shaped for the operator. */
  const checkFilterValue = (f: TemplateField, op: FilterOp, value: unknown, path: Path): void => {
    if (!SELECT_TYPES.has(f.type) || value === undefined) {
      return;
    }
    if (LIST_VALUE_OPS.has(op) && !Array.isArray(value)) {
      add(path, `${op} expects an array of option refs`);
      return;
    }
    if (SCALAR_VALUE_OPS.has(op) && Array.isArray(value)) {
      add(path, `${op} expects a single option ref`);
      return;
    }
    const refs = optionRefsOf(f);
    const values: unknown[] = Array.isArray(value) ? value : [value];
    values.forEach((v, i) => {
      if (typeof v !== 'string' || !refs.has(v)) {
        add(Array.isArray(value) ? [...path, i] : path, `unknown option ref for field ${f.ref}`);
      }
    });
  };

  const checkFieldRef = (ref: string | undefined, path: Path): TemplateField | undefined => {
    if (ref === undefined) {
      return undefined;
    }
    const f = fields.get(ref);
    if (!f) {
      add([...path, 'fieldRef'], `unknown field ref ${ref}`);
    }
    return f;
  };

  const checkLeaf = (leaf: TemplateConditionLeaf, path: Path): void => {
    const f = checkFieldRef('fieldRef' in leaf ? leaf.fieldRef : undefined, path);
    if (f) {
      checkFilterValue(f, leaf.op, leaf.value, [...path, 'value']);
    }
  };

  const walkCondition = (rule: TemplateConditionRule, path: Path): void => {
    if ('all' in rule) {
      rule.all.forEach((r, i) => {
        walkCondition(r, [...path, 'all', i]);
      });
      return;
    }
    if ('any' in rule) {
      rule.any.forEach((r, i) => {
        walkCondition(r, [...path, 'any', i]);
      });
      return;
    }
    if ('not' in rule) {
      walkCondition(rule.not, [...path, 'not']);
      return;
    }
    if (rule.type === 'user_in_role') {
      checkRole(rule.roleKey, [...path, 'roleKey']);
      return;
    }
    if (rule.type === 'field_matches') {
      checkLeaf(rule, path);
    }
  };

  const walkValidator = (v: TemplateValidator, path: Path): void => {
    switch (v.type) {
      case 'field_required':
        checkFieldRef('fieldRef' in v ? v.fieldRef : undefined, path);
        return;
      case 'field_matches':
        checkLeaf(v, path);
        return;
      case 'approval_required':
        if (v.approverRoleKey) {
          checkRole(v.approverRoleKey, [...path, 'approverRoleKey']);
        }
        return;
      default:
        return;
    }
  };

  const walkPostFunction = (p: TemplatePostFunction, path: Path): void => {
    if (p.type !== 'set_field') {
      return;
    }
    const f = checkFieldRef('fieldRef' in p ? p.fieldRef : undefined, path);
    if (f) {
      checkStoredValue(f, p.value, [...path, 'value']);
    }
  };

  const walkFilter = (node: TemplateFilterNode, path: Path): void => {
    if ('and' in node) {
      node.and.forEach((n, i) => {
        walkFilter(n, [...path, 'and', i]);
      });
      return;
    }
    if ('or' in node) {
      node.or.forEach((n, i) => {
        walkFilter(n, [...path, 'or', i]);
      });
      return;
    }
    if ('not' in node) {
      walkFilter(node.not, [...path, 'not']);
      return;
    }
    checkLeaf(node, path);
  };

  const walkQuery = (q: TemplateSearchRequest, path: Path): void => {
    if (q.filter) {
      walkFilter(q.filter, [...path, 'filter']);
    }
    q.sort.forEach((s, i) => {
      checkFieldRef('fieldRef' in s ? s.fieldRef : undefined, [...path, 'sort', i]);
    });
    if (q.groupBy) {
      checkFieldRef('fieldRef' in q.groupBy ? q.groupBy.fieldRef : undefined, [...path, 'groupBy']);
    }
    q.select?.forEach((s, i) => {
      if (typeof s !== 'string') {
        checkFieldRef(s.fieldRef, [...path, 'select', i]);
      }
    });
  };

  // Workflows
  const workflows = new Set<string>();
  doc.workflows.forEach((w, i) => {
    const path: Path = ['workflows', i];
    if (workflows.has(w.ref)) {
      add([...path, 'ref'], `duplicate workflow ref ${w.ref}`);
    }
    workflows.add(w.ref);
    const statuses = new Set<string>();
    const statusKeys = new Set<string>();
    w.statuses.forEach((s, j) => {
      if (statuses.has(s.ref)) {
        add([...path, 'statuses', j, 'ref'], `duplicate status ref ${s.ref}`);
      }
      statuses.add(s.ref);
      if (statusKeys.has(s.key)) {
        add([...path, 'statuses', j, 'key'], `duplicate status key ${s.key}`);
      }
      statusKeys.add(s.key);
    });
    if (!statuses.has(w.initialStatusRef)) {
      add([...path, 'initialStatusRef'], `unknown status ref ${w.initialStatusRef}`);
    }
    if (!w.statuses.some((s) => s.category === 'done')) {
      add([...path, 'statuses'], 'workflow needs at least one done status');
    }
    const transitionKeys = new Set<string>();
    let initialTransitions = 0;
    w.transitions.forEach((t, j) => {
      const tp: Path = [...path, 'transitions', j];
      if (transitionKeys.has(t.key)) {
        add([...tp, 'key'], `duplicate transition key ${t.key}`);
      }
      transitionKeys.add(t.key);
      if (!statuses.has(t.toStatusRef)) {
        add([...tp, 'toStatusRef'], `unknown status ref ${t.toStatusRef}`);
      }
      t.fromStatusRefs?.forEach((s, k) => {
        if (!statuses.has(s)) {
          add([...tp, 'fromStatusRefs', k], `unknown status ref ${s}`);
        }
      });
      if (t.isInitial) {
        initialTransitions += 1;
        if (t.toStatusRef !== w.initialStatusRef) {
          add([...tp, 'toStatusRef'], 'the initial transition must target initialStatusRef');
        }
      }
      walkCondition(t.conditions, [...tp, 'conditions']);
      t.validators.forEach((v, k) => {
        walkValidator(v, [...tp, 'validators', k]);
      });
      t.postFunctions.forEach((p, k) => {
        walkPostFunction(p, [...tp, 'postFunctions', k]);
      });
    });
    if (initialTransitions > 1) {
      add([...path, 'transitions'], 'at most one initial transition');
    }
  });

  // Issue types
  const issueTypes = new Set<string>();
  doc.issueTypes.forEach((t, i) => {
    if (issueTypes.has(t.ref)) {
      add(['issueTypes', i, 'ref'], `duplicate issue type ref ${t.ref}`);
    }
    issueTypes.add(t.ref);
    if (!workflows.has(t.workflowRef)) {
      add(['issueTypes', i, 'workflowRef'], `unknown workflow ref ${t.workflowRef}`);
    }
  });

  // Bindings
  const bound = new Set<string>();
  doc.bindings.forEach((b, i) => {
    const path: Path = ['bindings', i];
    const f = checkFieldRef(b.fieldRef, path);
    if (!f) {
      return;
    }
    if (bound.has(b.fieldRef)) {
      add([...path, 'fieldRef'], `field ${b.fieldRef} is bound twice`);
    }
    bound.add(b.fieldRef);
    if (b.defaultValue !== undefined) {
      checkStoredValue(f, b.defaultValue, [...path, 'defaultValue']);
    }
    b.issueTypeRefs?.forEach((r, j) => {
      if (!issueTypes.has(r)) {
        add([...path, 'issueTypeRefs', j], `unknown issue type ref ${r}`);
      }
    });
    b.access?.readRoles?.forEach((r, j) => {
      checkRole(r, [...path, 'access', 'readRoles', j]);
    });
    b.access?.editRoles?.forEach((r, j) => {
      checkRole(r, [...path, 'access', 'editRoles', j]);
    });
  });

  // Forms
  doc.forms.forEach((form, i) => {
    const path: Path = ['forms', i];
    if (form.issueTypeRef !== null && !issueTypes.has(form.issueTypeRef)) {
      add([...path, 'issueTypeRef'], `unknown issue type ref ${form.issueTypeRef}`);
    }
    const seen = new Set<string>();
    form.layout.sections.forEach((section, s) => {
      section.fields.forEach((fld, k) => {
        const fp: Path = [...path, 'layout', 'sections', s, 'fields', k];
        const key = 'key' in fld ? fld.key : fld.fieldRef;
        if (seen.has(key)) {
          add(fp, `field ${key} appears more than once`);
        }
        seen.add(key);
        if ('fieldRef' in fld) {
          const f = checkFieldRef(fld.fieldRef, fp);
          if (f && !bound.has(fld.fieldRef)) {
            add([...fp, 'fieldRef'], `field ${fld.fieldRef} is not bound to the project`);
          }
        }
        if (fld.visibleWhen) {
          checkLeaf(fld.visibleWhen, [...fp, 'visibleWhen']);
        }
      });
    });
  });

  // Permission grants
  const grantKeys = new Set<string>();
  doc.permissionGrants.forEach((g, i) => {
    const path: Path = ['permissionGrants', i];
    switch (g.granteeType) {
      case 'project_role':
        if (!g.granteeRoleKey) {
          add([...path, 'granteeRoleKey'], 'granteeRoleKey is required for project_role grants');
        } else {
          checkRole(g.granteeRoleKey, [...path, 'granteeRoleKey']);
        }
        break;
      case 'tenant_role':
        if (!g.granteeTenantRole) {
          add(
            [...path, 'granteeTenantRole'],
            'granteeTenantRole is required for tenant_role grants',
          );
        }
        break;
      case 'user':
      case 'group':
        add([...path, 'granteeType'], `${g.granteeType} grants are not portable`);
        break;
      default:
        if (g.granteeRoleKey !== undefined || g.granteeTenantRole !== undefined) {
          add(path, `${g.granteeType} grants carry no grantee`);
        }
    }
    const key = `${g.permission}|${g.granteeType}|${g.granteeRoleKey ?? g.granteeTenantRole ?? ''}`;
    if (grantKeys.has(key)) {
      add(path, 'duplicate grant');
    }
    grantKeys.add(key);
  });

  // Views
  doc.views.forEach((v, i) => {
    walkQuery(v.query, ['views', i, 'query']);
  });

  // SLA policies
  doc.slaPolicies.forEach((s, i) => {
    const path: Path = ['slaPolicies', i];
    for (const name of Object.keys(s.goals)) {
      if (!priorityNames.has(name)) {
        add([...path, 'goals', name], `unknown priority ${name}`);
      }
    }
    const groups = [
      ['startConditions', s.startConditions],
      ['pauseConditions', s.pauseConditions],
      ['stopConditions', s.stopConditions],
    ] as const;
    for (const [name, conditions] of groups) {
      conditions.forEach((c, j) => {
        walkCondition(c, [...path, name, j]);
      });
    }
  });

  return issues;
}

export const ProjectTemplateDocumentSchema = ProjectTemplateDocumentBaseSchema.superRefine(
  (doc, ctx) => {
    for (const issue of findTemplateIssues(doc)) {
      ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path });
    }
  },
);

/* ----------------------------- API DTOs ----------------------------- */

export const ProjectTemplateSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  name: z.string().min(1).max(80),
  description: z.string().max(500).nullable(),
  /** Project the template was exported from; null for imported/seeded templates. */
  sourceProjectId: z.uuid().nullable(),
  document: ProjectTemplateDocumentSchema,
  version: z.int().min(1),
  isSeeded: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ProjectTemplate = z.infer<typeof ProjectTemplateSchema>;

/** List projection: everything but the document. */
export const ProjectTemplateSummarySchema = ProjectTemplateSchema.omit({ document: true });
export type ProjectTemplateSummary = z.infer<typeof ProjectTemplateSummarySchema>;

export const ImportTemplateSchema = z.strictObject({
  name: z.string().min(1).max(80),
  description: z.string().max(500).nullable().default(null),
  document: ProjectTemplateDocumentSchema,
});
export type ImportTemplate = z.infer<typeof ImportTemplateSchema>;

/** `POST /projects/{id}/export-template` body. */
export const ExportTemplateSchema = z.strictObject({
  name: z.string().min(1).max(80),
  description: z.string().max(500).nullable().default(null),
});
export type ExportTemplate = z.infer<typeof ExportTemplateSchema>;
